import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uniq } from 'underscore';
import { v4 } from 'uuid';

import { CoordinateSystemModel, GeoidModel } from '../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../utils/variables';
import {
  CoordinateSystemItem,
  useCoordinateSystemsAndGeoidsQuery,
} from '../components/project-settings/projection';
import { ProjectSpatialReferenceContext } from '../state/project-reference-state';

const noGeoidId = v4();

// Extended type to include geoidId for geoid items
type GeoidItem = CoordinateSystemItem & {
  geoidId?: string;
};

/**
 * Maps a GeoidModel to multiple CoordinateSystemItem entries if it has multiple countryGeoids.
 * Each countryGeoid gets its own entry with a unique id but the same geoidId.
 */
const mapGeoids = (models: GeoidModel[]): GeoidItem[] => {
  const result: GeoidItem[] = [];

  for (const model of models) {
    if (model.countryGeoids && model.countryGeoids.length > 0) {
      // Create an entry for each countryGeoid
      for (const countryGeoid of model.countryGeoids) {
        result.push({
          id: `${model.id}-${countryGeoid.countryId}`,
          geoidId: model.id,
          name: countryGeoid.displayName ?? model.name,
          countryId: countryGeoid.countryId,
          sourceName: model.sourceName,
          sourceUrl: model.sourceUrl,
          licenseName: model.licenseName,
          licenseUrl: model.licenseUrl,
        });
      }
    } else {
      // No countryGeoids, create a single entry with default id
      result.push({
        id: model.id,
        geoidId: model.id,
        name: model.name,
        countryId: null,
        sourceName: model.sourceName,
        sourceUrl: model.sourceUrl,
        licenseName: model.licenseName,
        licenseUrl: model.licenseUrl,
      });
    }
  }

  return result;
};

/**
 * Extracts the geoidId from a GeoidItem. Returns the geoidId if present, otherwise returns undefined.
 */
const getGeoidIdFromItem = (item: CoordinateSystemItem | undefined): string | undefined => {
  if (!item) {
    return undefined;
  }
  const geoidItem = item as GeoidItem;
  return geoidItem.geoidId;
};

export const useCoordinateSystemsAndGeoidsState = () => {
  const { t } = useTranslation();
  // Context is typed in project-reference-state; useContext sees it as any when imported
  const spatialReference = useContext(ProjectSpatialReferenceContext);
  const projectCoordinateSystemFromContext: CoordinateSystemModel | undefined =
    spatialReference?.coordinateSystem;
  const projectGeoidFromContext: GeoidModel | undefined = spatialReference?.geoid;
  const [selectedCoordinateSystem, setSelectedCoordinateSystem] = useState<
    CoordinateSystemItem | undefined
  >();
  const [selectedGeoid, setSelectedGeoid] = useState<CoordinateSystemItem | undefined>();
  const [geoids, setGeoids] = useState<CoordinateSystemItem[]>([]);
  const [coordinateSystems, setCoordinateSystems] = useState<CoordinateSystemItem[]>([]);

  const { data, isLoading: isLoadingCoordinateSystems } = useCoordinateSystemsAndGeoidsQuery();

  // Fetch raw geoids to expand countryGeoids
  const { data: rawGeoidsData, isLoading: isLoadingGeoids } = useQuery({
    queryKey: ['getRawGeoids'],
    queryFn: async () => {
      const response = await SkyMapAxiosServiceFactory.instance
        .createGeoidServiceV1()
        .getGeoids({});
      return response.data;
    },
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isLoading = isLoadingCoordinateSystems || isLoadingGeoids;

  useEffect(() => {
    if (!isDefined(data) || !isDefined(rawGeoidsData)) {
      return;
    }

    const projectCoordinateSystem = isDefined(projectCoordinateSystemFromContext)
      ? {
          id: projectCoordinateSystemFromContext.id,
          name: `${projectCoordinateSystemFromContext.name} (${projectCoordinateSystemFromContext.authority})`,
          countryId: null,
        }
      : undefined;

    // Map all geoids, expanding those with multiple countryGeoids
    const mappedGeoids = mapGeoids(rawGeoidsData);

    // Find the project geoid in the mapped list
    const projectGeoidId = projectGeoidFromContext?.id;
    let projectGeoid: GeoidItem | undefined;

    if (isDefined(projectGeoidId)) {
      // Try to find a matching geoid item (prefer one with countryId matching project if available)
      projectGeoid = mappedGeoids.find((g) => g.geoidId === projectGeoidId);

      // If not found, create a default entry
      if (!projectGeoid && projectGeoidFromContext) {
        projectGeoid = {
          id: projectGeoidId,
          geoidId: projectGeoidId,
          name: projectGeoidFromContext.name,
          countryId: null,
        };
      }
    }

    const noGeoid: GeoidItem = {
      countryId: null,
      id: noGeoidId,
      name: t('notSelected', { ns: 'common' }),
    };

    setSelectedCoordinateSystem(projectCoordinateSystem);
    setSelectedGeoid(projectGeoid ?? noGeoid);

    setCoordinateSystems(
      uniq(
        projectCoordinateSystem
          ? [
              projectCoordinateSystem,
              ...data.coordinateSystems.filter((x) => x.id !== projectCoordinateSystem.id),
            ]
          : data.coordinateSystems,
        false,
        (x) => x.id,
      ),
    );

    // Filter out project geoid entries (by geoidId) and add project geoid at the top if it exists
    const allGeoids = mappedGeoids.filter(
      (g) => !projectGeoid || g.geoidId !== projectGeoid.geoidId,
    );

    if (projectGeoid) {
      allGeoids.unshift(projectGeoid);
    }

    allGeoids.unshift(noGeoid);

    setGeoids(allGeoids);
  }, [data, rawGeoidsData, t, projectCoordinateSystemFromContext, projectGeoidFromContext]);

  return {
    selectedCoordinateSystem,
    setSelectedCoordinateSystem,
    selectedGeoid,
    setSelectedGeoid,
    coordinateSystems,
    geoids,
    isLoading,
    getGeoidIdFromItem,
  };
};
